import type { EntityTransform, Vec3 } from "@models/data/entityData";
import type { SelectionSetPose } from "@models/data/selectionSetTransform";

/** One channel of an entity's stored transform. */
export type TransformChannel = keyof EntityTransform;

/**
 * Renderer-side geometry seam for Unity's vertex-accurate floor placement.
 * The headless model owns persistence; the viewport owns rendered bounds and
 * converts the world-space drop back into snapshot-local coordinates.
 */
export interface IPlacementGeometryHost {
  calculateGroundedPosition(sceneId: string, snapshotId: string): Vec3 | null;
  /**
   * The stored local position that puts `snapshotId` at the centre of its
   * PARENT's rendered bounds — Unity's `OriginContextViewModel.PlaceAtModelCenter`:
   * the world-space bounds of every renderer under the parent (hidden ones
   * included, as `includeInactive: true`; gizmos excluded), brought into the
   * parent's frame. Null when nothing under the parent renders.
   */
  calculateParentBoundsCenterPosition(sceneId: string, snapshotId: string): Vec3 | null;
}

/**
 * The card renderer's half of {@link IPlacementGeometryHost}: a card is not an
 * entity snapshot, so its rendered face lives in `MediaTileRenderer`. Answers
 * the card's stored position moved so its lowest visible point rests on the
 * ground — Unity's `PlacementModelBase.DropOnTheFloor` on a `TextTile` root.
 */
export interface IMediaTileGeometryHost {
  calculateGroundedMediaTilePosition(sceneId: string, tileId: string): Vec3 | null;
}

/**
 * Viewport-side seam for moving a selection SET in world space — implemented
 * by `TransformGizmoController`, the only thing holding the members' rendered
 * frames. Unity's wrapper carries a set edit to its members in world space
 * (`MultiExposeToEditor.cs:222-286`); a PART's stored transform is relative to
 * its parent, so applying the set's delta to stored transforms is only right
 * for root members.
 */
export interface ISelectionSetTransformHost {
  /**
   * Move the members so the set's pose becomes `pose`, then persist the
   * members and the set row. `null` when the host cannot do it (no member is
   * rendered); the caller then falls back.
   */
  transformSelectionSet(
    sceneId: string,
    selectionSetId: string,
    memberIds: Iterable<string>,
    pose: SelectionSetPose,
  ): Promise<void> | null;
}

export interface IPlacementModel {
  attachGeometryHost(host: IPlacementGeometryHost | null): void;
  attachMediaTileGeometryHost(host: IMediaTileGeometryHost | null): void;
  attachSelectionSetTransformHost(host: ISelectionSetTransformHost | null): void;
  /** The registered host's world-space set move, or `null` when there is none. */
  tryTransformSelectionSet(
    sceneId: string,
    selectionSetId: string,
    memberIds: Iterable<string>,
    pose: SelectionSetPose,
  ): Promise<void> | null;
  /**
   * The Placement page's fields: write the given channels. Every write and
   * reset below is one undo step and writes nothing when it would change
   * nothing.
   */
  setTransformAsync(sceneId: string, snapshotId: string, transform: Partial<EntityTransform>): Promise<void>;
  placeOnGroundAsync(sceneId: string, snapshotId: string): Promise<void>;
  resetPositionAsync(sceneId: string, snapshotId: string): Promise<void>;
  resetRotationAsync(sceneId: string, snapshotId: string): Promise<void>;
  resetTransformAsync(sceneId: string, snapshotId: string): Promise<void>;
  resetScaleAsync(sceneId: string, snapshotId: string): Promise<void>;
  /**
   * Reset `channels` of several entities as ONE undo step and one batch write.
   * This is a temporary multi-selection's Reset: Unity resets every member
   * inside one `BeginEditPosition` / `EndEditPosition`
   * (`SelectedGameObjectModel.ResetMultipleObjects`).
   */
  resetTransformsAsync(
    sceneId: string,
    snapshotIds: ReadonlyArray<string>,
    channels: ReadonlyArray<TransformChannel>,
  ): Promise<void>;
  alignToCadOriginsAsync(sceneId: string, snapshotId: string): Promise<void>;
  /**
   * The Origin page's "Place at model center": move the `Origin` marker to the
   * centre of its model's rendered bounds. One undo step.
   */
  placeOriginAtModelCenterAsync(sceneId: string, originSnapshotId: string): Promise<void>;
  /**
   * The Origin page's "Place at CAD origin": move the `Origin` marker onto the
   * model's `Root` — the file's own coordinate origin
   * (`SelectedGameObjectModel.PlaceAtCADOrigin`). One undo step; a model with
   * no `Root` row (a legacy layout) is left alone, as upstream does.
   */
  placeOriginAtCadOriginAsync(sceneId: string, originSnapshotId: string): Promise<void>;
}

export const PLACEMENT_MODEL = Symbol.for("Campfire.PlacementModel");
